import {useState, useEffect, useCallback, useRef} from "react";
import {
  chatConnectedResponse,
  ChatMessage,
  ChatRole,
  successLLMResponse,
  successSTTResponse,
} from "../logic/types/chat.types";
import {useSocket} from "src/hooks/socket/useSocket";
import {ConnectionState} from "src/hooks/socket/socket.type";
import {useAudioContext} from "src/hooks/audioContext/useAudioContext";
import {useVoicePlayer} from "src/hooks/voicePlayer/useVoicePlayer";
import {RECORDING_MAX_TIME} from "../assets/constants/chatOptions";
import {Artist, convertArtistToType} from "src/utils/artist.utils";
import {SessionAPI} from "src/services/session/logic/api/session.api";
import {SESSION_STATUS} from "src/services/session/logic/types/session.type";
import {isServiceUnavailableError} from "src/utils/error.utils";

const SOCKET_EMIT_EVENTS = {
  SEND_AUDIO: "audio",
  SEND_TEXT: "text",
  HEARTBEAT: "heartbeat",
  EXIT: "exit",
  PAUSE: "pause",
};

const SOCKET_ON_EVENTS = {
  CHAT_CONNECTED: "chat:connected",
  CHAT_ERROR: "chat:error",
  STT_SUCCESS: "stt:done",
  STT_ERROR: "chat:stt-error",
  LLM_SUCCESS: "llm:done",
  LLM_ERROR: "chat:llm-error",
  LLM_TIMEOUT: "llm:timeout",
  EMERGENCY: "emergency",
};

type UseChatParams = {
  artist: Artist;
  character: string;
  language: string;
  onExpire: () => void;
  onSocketError: () => void;
  onForegroundSessionNone: () => void;
  onNetworkTimeout: () => void;
  onServiceUnavailable: () => void;
  onEmergency: () => void;
};

export const useChat = ({
  artist,
  character,
  language,
  onExpire,
  onForegroundSessionNone,
  onSocketError,
  onNetworkTimeout,
  onServiceUnavailable,
  onEmergency,
}: UseChatParams) => {
  const {connectionState, connect, disconnect, on, off, emit} = useSocket();
  const {
    audioState,
    startRecording,
    stopRecording,
    convertToUint8Array,
    endAudioRecording,
  } = useAudioContext();

  // 메세지 정보
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  // 입력 값
  const [inputValue, setInputValue] = useState<string>("");
  // 질문 중 (녹음 진행 or 채팅 전송 ~ STT 반환 or 채팅 전송)
  const [isProcessingQuestion, setIsProcessingQuestion] =
    useState<boolean>(false);
  // 답변 중 (STT 완료 or 채팅 전송 완료 ~ 캐릭터 답변 생성 완료)
  const [isProcessingAnswer, setIsProcessingAnswer] = useState<boolean>(false);
  const [recordingTimeRemaining, setRecordingTimeRemaining] =
    useState<number>(0);

  // 이벤트(chat:connected)로 수신한 쿠폰 만료 시각 (Unix ms). null이면 아직 연결 전이므로 rAF 타이머를 시작하지 않는다.
  const [usageEndsAt, setUsageEndsAt] = useState<number | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);

  // requestAnimationFrame 기반 타이머: cleanup 시 cancelAnimationFrame용
  const countdownRafHandleRef = useRef<number | null>(null);

  // 소켓 연결 상태의 최신값에 접근하기 위한 미러링 변수
  const connectionStateRef = useRef<ConnectionState>(connectionState);

  // 녹음이 완전히 시작되기 전에 중단 요청이 먼저 들어온 경우를 감지하는 플래그
  const isRecordingInterruptedRef = useRef<boolean>(false);
  // 초 단위가 바뀔 때만 setState 하기 위해 마지막으로 표시한 남은 초 저장
  const lastRemainingSecondsRef = useRef<number>(-1);

  const artistType = convertArtistToType(artist);

  const {playVoiceForText, stopVoice, activateVoice, isVoiceActivated} =
    useVoicePlayer(artist, character);

  const chatSocketConnect = async (): Promise<void> => {
    try {
      await connect(`${process.env.REACT_APP_SOCKET_SERVER_URL}/chat`, {
        transports: ["websocket"],
        withCredentials: true,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const subscribeChatSocketEvent = (): void => {
    on(SOCKET_ON_EVENTS.CHAT_CONNECTED, (data: chatConnectedResponse) => {
      console.log(`${SOCKET_ON_EVENTS.CHAT_CONNECTED} :: received`);
      console.log(data);
      const remaining = data.coupon?.remainingSeconds ?? 0;
      setUsageEndsAt(Date.now() + remaining * 1000);
      setRemainingSeconds(remaining);
    });

    on(SOCKET_ON_EVENTS.CHAT_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.CHAT_ERROR} :: received`);
      console.log(data);
      onSocketError();
    });

    on(SOCKET_ON_EVENTS.STT_SUCCESS, (data: successSTTResponse) => {
      console.log(`${SOCKET_ON_EVENTS.STT_SUCCESS} :: received`);
      const userMessage: ChatMessage = {
        content: data.text,
        role: ChatRole.USER,
        ts: Date.now(),
      };
      setMessages([userMessage]);
      setIsProcessingQuestion(false);
      setIsProcessingAnswer(true);
      endAudioRecording();
    });

    on(SOCKET_ON_EVENTS.STT_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.STT_ERROR} :: received`);
      console.log(data);
      setIsProcessingQuestion(false);
      setIsProcessingAnswer(false);
      onSocketError();
      endAudioRecording();
    });

    on(SOCKET_ON_EVENTS.LLM_SUCCESS, (data: successLLMResponse) => {
      const assistantMessage: ChatMessage = {
        content: data.response,
        role: ChatRole.ASSISTANT,
        ts: Date.now(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsProcessingAnswer(false);
      playVoiceForText(data.response, data.intent);
    });

    on(SOCKET_ON_EVENTS.LLM_ERROR, (data: unknown) => {
      console.log(`${SOCKET_ON_EVENTS.LLM_ERROR} :: received`);
      console.log(data);
      setMessages([]);
      setIsProcessingQuestion(false);
      setIsProcessingAnswer(false);
      onSocketError();
    });

    on(SOCKET_ON_EVENTS.LLM_TIMEOUT, () => {
      console.log(`${SOCKET_ON_EVENTS.LLM_TIMEOUT} :: received`);
      setMessages([]);
      setIsProcessingQuestion(false);
      setIsProcessingAnswer(false);
      onSocketError();
    });

    on(SOCKET_ON_EVENTS.EMERGENCY, () => {
      console.log(`${SOCKET_ON_EVENTS.EMERGENCY} :: received`);
      stopVoice();
      endAudioRecording();
      disconnect();
      onEmergency();
    });
  };

  const unsubscribeChatSocketEvent = (): void => {
    off(SOCKET_ON_EVENTS.CHAT_CONNECTED);
    off(SOCKET_ON_EVENTS.CHAT_ERROR);
    off(SOCKET_ON_EVENTS.STT_SUCCESS);
    off(SOCKET_ON_EVENTS.STT_ERROR);
    off(SOCKET_ON_EVENTS.LLM_SUCCESS);
    off(SOCKET_ON_EVENTS.LLM_ERROR);
    off(SOCKET_ON_EVENTS.LLM_TIMEOUT);
    off(SOCKET_ON_EVENTS.EMERGENCY);
  };

  const handleSendChatMessage = (message: string): void => {
    if (connectionState !== ConnectionState.CONNECTED) {
      onNetworkTimeout();
      return;
    }

    if (message.trim() === "") {
      return;
    }

    stopVoice();

    emit(SOCKET_EMIT_EVENTS.SEND_TEXT, {
      text: message.trim(),
      type: artistType,
      slave: character,
      language,
    });
    setIsProcessingAnswer(true);

    const userMessage: ChatMessage = {
      content: message.trim(),
      role: ChatRole.USER,
      ts: Date.now(),
    };

    setMessages([userMessage]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value);
  };

  const handleSubmitInput = (): void => {
    handleSendChatMessage(inputValue.trim());
    setInputValue("");
  };

  const startAudioRecording = async (): Promise<boolean> => {
    isRecordingInterruptedRef.current = false;
    stopVoice();
    setRecordingTimeRemaining(RECORDING_MAX_TIME);
    try {
      await startRecording();

      if (isRecordingInterruptedRef.current) {
        stopRecording(); // 리소스 정리
        endAudioRecording();
        setRecordingTimeRemaining(0);
        return false;
      }

      setMessages([]);
      setIsProcessingQuestion(true);
      return true;
    } catch (error) {
      setRecordingTimeRemaining(0);
      throw error;
    }
  };

  const stopAudioRecording = (): void => {
    stopVoice();
    isRecordingInterruptedRef.current = true;

    const audioData = stopRecording();
    if (!audioData) {
      setIsProcessingQuestion(false);
      endAudioRecording();
      return;
    }

    const uint8Array = convertToUint8Array(audioData, true);

    if (!uint8Array || uint8Array.length === 0) {
      setIsProcessingQuestion(false);
      setIsProcessingAnswer(false);
      onSocketError();
      endAudioRecording();
      return;
    }

    isRecordingInterruptedRef.current = false; // emit이 발생하므로 abort 아님
    emit(SOCKET_EMIT_EVENTS.SEND_AUDIO, {
      audio: uint8Array,
      type: artistType,
      slave: character,
      language,
    });
  };

  const handleStartAudioRecording = useCallback(async () => {
    if (connectionState !== ConnectionState.CONNECTED) {
      onNetworkTimeout();
      return;
    }

    if (!isVoiceActivated) {
      try {
        await activateVoice();
      } catch (error) {
        console.error("Failed to activate audio:", error);
        throw error;
      }
    }

    const started = await startAudioRecording();
    if (!started) return;
  }, [startAudioRecording, isVoiceActivated, activateVoice, endAudioRecording]);

  const handleStopAudioRecording = useCallback(() => {
    stopAudioRecording();
    setRecordingTimeRemaining(0);
  }, [stopAudioRecording]);

  // 포그라운드 재진입 감지 (모바일 백그라운드 복귀 + PC 탭 전환 복귀)
  const handleVisibilityChange = async (): Promise<void> => {
    if (document.visibilityState === "hidden") {
      console.log("[useChat] 백그라운드 진입 감지");
      return;
    }

    console.log("[useChat] 포그라운드 진입 감지 → 연결 상태 확인");

    // ① 진입 시점 가드: 이미 연결됨 또는 자동 재연결 진행 중 → 개입 불필요
    if (
      connectionStateRef.current === ConnectionState.CONNECTED ||
      connectionStateRef.current === ConnectionState.RECONNECTING ||
      connectionStateRef.current === ConnectionState.CONNECTING
    ) {
      console.log(
        `[useChat] 소켓 상태 ${connectionStateRef.current} → 세션 API 호출 생략`,
      );
      return;
    }

    // DISCONNECTED(자동 재연결 5회 실패) → 세션 확인 후 수동 재연결
    console.log("[useChat] 소켓 DISCONNECTED → 세션 상태 확인");

    const type = convertArtistToType(artist);
    try {
      const {status} = await SessionAPI.fetchSessionStatus({query: {type}});

      // ② API 응답 대기 중 상태 변화 가능 → 재확인 (race condition 방지)
      // await 이후 ref를 새 변수로 재샘플링 (TS narrowing 리셋)
      const stateAfterFetch = connectionStateRef.current as ConnectionState;
      if (
        stateAfterFetch === ConnectionState.CONNECTED ||
        stateAfterFetch === ConnectionState.RECONNECTING
      ) {
        console.log(
          "[useChat] API 응답 대기 중 소켓 재연결 완료 → 수동 재연결 스킵",
        );
        return;
      }

      if (status === SESSION_STATUS.NONE) {
        console.log("[useChat] 세션 상태: NONE → waiting 페이지로 이동");
        onForegroundSessionNone();
        return;
      }

      if (status === SESSION_STATUS.PLAY) {
        console.log("[useChat] 세션 상태: PLAY → 소켓 재연결");
        await chatSocketConnect();
        return;
      }

      console.log(`[useChat] 세션 상태: ${status} (발생할 수 없는 상황)`);
    } catch (error) {
      if (isServiceUnavailableError(error)) {
        onServiceUnavailable();
        return;
      }
      console.error("[useChat] 세션 상태 조회 실패", error);
    }
  };

  // connectionState → ref 동기화 (stale closure 방지)
  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  const clear = async () => {
    if (connectionStateRef.current === ConnectionState.CONNECTED) {
      emit(SOCKET_EMIT_EVENTS.PAUSE, "");
    }

    await new Promise((resolve) => setTimeout(resolve, 300));
    stopVoice();
    disconnect();
  };

  // 마운트 시 자동 소켓 연결 / 언마운트 시 EXIT 전송 후 정리
  useEffect(() => {
    chatSocketConnect();

    return () => {
      clear();
    };
  }, []);

  // 연결 상태에 따른 자동 이벤트 구독
  useEffect(() => {
    if (connectionState === ConnectionState.CONNECTED) {
      console.log("소켓 연결 성공 :: 이벤트를 구독합니다.");
      subscribeChatSocketEvent();
    } else {
      console.log("소켓 연결 실패 :: 이벤트 구독을 해제합니다.");
      unsubscribeChatSocketEvent();
    }

    return () => {
      unsubscribeChatSocketEvent();
    };
  }, [connectionState]);

  // 소켓 연결 유지를 위해 10초마다 heartbeat 전송
  useEffect(() => {
    if (connectionState !== ConnectionState.CONNECTED) {
      return;
    }

    const interval = setInterval(() => {
      emit(SOCKET_EMIT_EVENTS.HEARTBEAT, "");
    }, 10000);

    return () => clearInterval(interval);
  }, [connectionState]);

  // 쿠폰 만료 카운트다운: usageEndsAt 기준으로 rAF 루프 실행
  useEffect(() => {
    if (usageEndsAt === null) {
      return;
    }

    if (connectionState !== ConnectionState.CONNECTED) {
      return;
    }

    // setInterval 대신 rAF 사용: 입력/녹음 등으로 메인 스레드가 바빠도
    // 다음 프레임에서 Date.now() 기준으로 갱신되어 타이머가 멈춘 것처럼 보이는 현상 완화
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((usageEndsAt - Date.now()) / 1000),
      );
      if (remaining !== lastRemainingSecondsRef.current) {
        lastRemainingSecondsRef.current = remaining;
        setRemainingSeconds(remaining);
      }
      if (remaining === 0) {
        onExpire();
        return;
      }
      countdownRafHandleRef.current = requestAnimationFrame(tick);
    };
    countdownRafHandleRef.current = requestAnimationFrame(tick);
    return () => {
      if (countdownRafHandleRef.current !== null) {
        cancelAnimationFrame(countdownRafHandleRef.current);
        countdownRafHandleRef.current = null;
      }
    };
  }, [usageEndsAt, connectionState, onExpire]);

  // 녹음 중 1초마다 recordingTimeRemaining 감소, 0 도달 시 자동 중지
  useEffect(() => {
    if (audioState !== "recording") {
      return;
    }

    const timer = setInterval(() => {
      setRecordingTimeRemaining((prev) => {
        if (prev <= 1) {
          handleStopAudioRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [audioState]);

  // 백그라운드/포그라운드 전환 감지 리스너 등록·해제
  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return {
    connectionState,
    messages,
    audioState,
    handleStartAudioRecording,
    handleStopAudioRecording,
    isProcessingAnswer,
    isProcessingQuestion,
    isVoiceActivated,
    activateVoice,
    recordingTimeRemaining,
    inputValue,
    handleInputChange,
    handleSubmitInput,
    remainingSeconds,
  };
};
